<template>
  <div>
    <Profile
      v-if="stepShown === 0"
      :answers="answers.profile"
      :is-end="method.kind === 'join'"
      :is-next-loading="isCompletingOnboarding"
      @next-step="nextStep"
      @prev-step="prevStep"
      @answers="
        (_question: string | undefined, answer: string | undefined) => {
          answers.profile.fullName = answer;
        }
      "
    />

    <UseCase
      v-else-if="stepShown === 1"
      v-model:answer="answers.useCase"
      :is-end="answers.useCase === 'individual' && !willConfirmPlan"
      :is-next-loading="isCompletingOnboarding"
      @prev-step="prevStep"
      @next-step="nextStep"
    />

    <Organization
      v-else-if="stepShown === 2"
      :answers="answers.organization"
      :is-next-loading="isCreatingOrganization"
      :error="organizationCreationError"
      @next-step="nextStep"
      @prev-step="prevStep"
      @answers="
        (question: string | undefined, answer: string | undefined) => {
          if (question === 'name') answers.organization.name = answer;
          else if (question === 'size') answers.organization.size = answer;
          else answers.organization.industry = answer;
        }
      "
    />

    <Invite
      v-else-if="stepShown === 3"
      :is-next-loading="isCompletingOnboarding"
      @next-step="nextStep"
      @prev-step="prevStep"
    />

    <Channel
      v-else-if="stepShown === 4"
      v-model:answer="answers.channel"
      :is-end="!willConfirmPlan"
      :is-next-loading="isCompletingOnboarding"
      @next-step="nextStep"
      @prev-step="prevStep"
    />

    <ConfirmPlan
      v-else-if="stepShown === 5"
      :org-id="planStepOrgId"
      :use-case="planStepUseCase"
      :preselected-plan="preselectedPlan"
      :preselected-interval="preselectedInterval"
      :is-next-loading="isCompletingOnboarding"
      @next-step="nextStep"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, reactive, ref } from "vue";

import type { OrgId } from "@ctrl/controller-rpc";

import assert from "@/assert";
import * as inject from "@/components/inject";
import { PERSONAL_ORG_APP_SCREEN_IDS } from "@/components/menu/menuItems";
import Channel, { ChannelType } from "@/components/onboarding/Channel.vue";
import ConfirmPlan, {
  ConfirmPlanUseCase,
  resolveConfirmPlanEntries,
} from "@/components/onboarding/ConfirmPlan.vue";
import {
  OnboardingUseCase,
  useOnboarding,
} from "@/components/onboarding/index";
import Invite from "@/components/onboarding/Invite.vue";
import Organization from "@/components/onboarding/Organization.vue";
import { appendPostSetupQueuedOperation } from "@/components/onboarding/postSetupQueue";
import Profile from "@/components/onboarding/Profile.vue";
import UseCase, { UseCaseType } from "@/components/onboarding/UseCase.vue";
import { createOrganization } from "@/components/organizations";
import { metrics } from "@/metrics";
import { browser } from "@/services/browser";
import type { BillingInterval } from "@/utils/billing";
import { FORCED_FREEMIUM, FREE_PLAN_NAME } from "@/utils/billing";

export type OnboardingMethod =
  | { kind: "self" } // self sign up
  | { kind: "join"; orgId: string }; // sign up through organization invitation link

type OnboardingAnswers = {
  useCase: UseCaseType | undefined;
  channel: ChannelType | undefined;
  organization: {
    name: string | undefined;
    size: string | undefined;
    industry: string | undefined;
  };
  profile: {
    fullName: string | undefined;
  };
};

export default defineComponent({
  components: {
    UseCase,
    Invite,
    Organization,
    Profile,
    Channel,
    ConfirmPlan,
  },
  props: {
    method: {
      type: Object as PropType<OnboardingMethod>,
      required: true,
    },
  },
  emits: {
    "onboarding-completed": () => true,
  },
  setup(props, { emit }) {
    const controller = inject.controller();
    const menu = inject.menu();
    const profile = inject.profile();
    const billing = inject.billing();
    const isBetaTester = inject.isBetaTester();
    const logger = inject.logger("Onboarding");
    const baseLogger = logger.base();

    // Watchers will not wait for the last promise returned by their handler
    // to complete before firing the handler again.
    // Hence, we use `lastMetricPromise` to guarantee that metrics will be
    // sent in the appropriate order.
    const lastMetricPromise = ref<Promise<void> | null>(null);

    const stepShown = ref(0);

    // Plan (and billing interval) preselected on the /pricing page the user
    // signed up from, persisted by App.vue at launch. The final onboarding
    // step offers the plans valid for the path taken, narrowed to the
    // preselected plan when it is one of them. Consume the values right away
    // so an abandoned onboarding does not resurface them on a later launch
    // without a plan in the URL.
    const storedPlan = browser.store.get<unknown>(
      "temporary:preselectedPlan",
      undefined
    );
    const preselectedPlan =
      typeof storedPlan === "string" ? storedPlan : undefined;
    const storedInterval = browser.store.get<unknown>(
      "temporary:preselectedInterval",
      undefined
    );
    const preselectedInterval: BillingInterval =
      storedInterval === "month" ? "month" : "year";
    browser.store.unset("temporary:preselectedPlan");
    browser.store.unset("temporary:preselectedInterval");

    const PLAN_STEP = 5;

    const answers = reactive<OnboardingAnswers>({
      useCase: undefined,
      channel: undefined,
      organization: {
        name: undefined,
        size: undefined,
        industry: undefined,
      },
      profile: {
        fullName: undefined,
      },
    });

    const isTeam = computed(() => answers.useCase === "team");
    const hasInitialized = ref(false);
    const isCompletingOnboarding = ref(false);
    const isCreatingOrganization = ref(false);
    const isAdvancingStep = ref(false);
    const createdOrganizationId = ref<OrgId | null>(null);
    const organizationCreationError = ref<string | undefined>(undefined);

    const personalOrgId = computed(
      () => profile.value.user.personal.organizationId
    );

    // The path taken decides which plans the plan step displays and which
    // organization they apply to: the created team organization on the team
    // path, the personal organization on the individual path. Only read once
    // the use case has been answered; the "individual" fallback covers the
    // unreachable undefined case.
    const planStepUseCase = computed<ConfirmPlanUseCase>(() =>
      answers.useCase === "team" ? "team" : "individual"
    );
    const planStepOrgId = computed(() =>
      answers.useCase === "team" && createdOrganizationId.value !== null
        ? createdOrganizationId.value
        : personalOrgId.value
    );

    // Whether the plan step follows the current path. When freemium is off,
    // every self-signup goes through it; when freemium is on, only users who
    // preselected a paid plan on the website's pricing page do — selecting
    // the Free plan there (?plan=free) needs no confirmation, the account is
    // on it already. Either way it requires at least one plan valid for the
    // path taken (per-user plans on the individual path, organization plans
    // on the team path) — otherwise, e.g. the pricing catalog has not
    // loaded, the previous step launches the app directly. The steps that
    // can precede the plan step also read this to label their button
    // Continue instead of Launch.
    const willConfirmPlan = computed(
      () =>
        props.method.kind === "self" &&
        (!FORCED_FREEMIUM ||
          (preselectedPlan !== undefined &&
            preselectedPlan.toLowerCase() !== FREE_PLAN_NAME.toLowerCase())) &&
        resolveConfirmPlanEntries(
          billing.value,
          planStepUseCase.value,
          preselectedInterval
        ).length > 0
    );

    // Send metrics about the onboarding process.
    //
    // Note that this is not the first IDENTIFY call made during this session.
    // App.setup() always identifies the user right after login.
    const sendOnboardingMetrics = () => {
      const properties = {
        onboarding_type: answers.useCase || null,
        onboarding_size: answers.organization.size || null,
        onboarding_industry: answers.organization.industry || null,
        onboarding_channel: answers.channel || null,
        full_name: answers.profile.fullName || null,
      };
      logger.info("onboarding properties", properties);
      metrics.segment.identify(profile.value.user.accountId, properties, false);
    };

    const customizeMenu = async () => {
      try {
        const contactsItem = menu.visibleItems.value.find(
          (item) =>
            item.target.kind === "app_screen" &&
            item.target.value === PERSONAL_ORG_APP_SCREEN_IDS.Contacts
        );
        if (contactsItem) {
          await menu.toggleAppScreenVisibility(contactsItem);
        }
      } catch (e) {
        logger.error("hide menu items on onboarding complete failed", e);
      }
    };

    const completeOnboarding = async (usecase: UseCaseType) => {
      if (isCompletingOnboarding.value) return;
      isCompletingOnboarding.value = true;

      // Only record session replays for non-beta-tester brand-new accounts
      if (!isBetaTester.value) {
        metrics.logrocket.initialize();
        metrics.logrocket.identify(profile.value.user.accountId);
      }

      try {
        sendOnboardingMetrics();
      } catch (e) {
        logger.error("sendOnboardingMetrics failed", e);
      }

      // Perform account initialization
      try {
        if (!hasInitialized.value) {
          let onboardingUseCase: OnboardingUseCase;
          switch (usecase) {
            case "team": {
              const orgId =
                props.method.kind === "join"
                  ? props.method.orgId
                  : createdOrganizationId.value;
              assert(orgId != null, "Organization ID is required");
              onboardingUseCase = {
                type: "team",
                organizationId: orgId,
              };
              break;
            }
            case "individual":
              onboardingUseCase = { type: "individual" };
              break;
            default:
              throw new Error(`Invalid use case: ${usecase}`);
          }
          await useOnboarding(
            controller,
            profile.value,
            menu,
            onboardingUseCase
          );
          hasInitialized.value = true;
        }
      } catch (e) {
        logger.error("useOnboarding failed", e);
      }

      await customizeMenu();

      metrics.segment.page("Onboarding", {
        step: "complete",
        usecase: usecase,
      });

      window.setTimeout(() => {
        logger.info("emit onboarding-completed");
        emit("onboarding-completed");
      }, 0);
    };

    const nextStep = async () => {
      if (isAdvancingStep.value) return;
      isAdvancingStep.value = true;
      try {
        logger.info("onboarding nextStep", {
          stepShown: stepShown.value,
          useCase: answers.useCase,
        });

        if (stepShown.value === 0 && props.method.kind === "join") {
          await completeOnboarding("team");
          return;
        }

        if (stepShown.value === 1 && answers.useCase === "individual") {
          if (willConfirmPlan.value) {
            stepShown.value = PLAN_STEP;
            return;
          }
          await completeOnboarding(answers.useCase);
          return;
        }
        if (stepShown.value === 4 && answers.useCase === "team") {
          if (willConfirmPlan.value) {
            stepShown.value = PLAN_STEP;
            return;
          }
          await completeOnboarding(answers.useCase);
          return;
        }
        if (stepShown.value === PLAN_STEP) {
          assert(answers.useCase !== undefined, "Use case is required");
          await completeOnboarding(answers.useCase);
          return;
        }

        // After organization step, create the organization before the invite step.
        if (stepShown.value === 2 && answers.useCase === "team") {
          if (createdOrganizationId.value == null) {
            const name = answers.organization.name ?? "";
            isCreatingOrganization.value = true;
            organizationCreationError.value = undefined;
            try {
              logger.debug("creating organization…", name);
              createdOrganizationId.value = await createOrganization(
                controller,
                baseLogger,
                name,
                { initializeDefaultWorkspace: true }
              );
              if (props.method.kind === "self") {
                appendPostSetupQueuedOperation({
                  kind: "setup_organization",
                  organizationId: createdOrganizationId.value,
                });
              }
              logger.debug("organization created", createdOrganizationId.value);
            } catch (e) {
              logger.error("organization create failed", e);
              return;
            } finally {
              isCreatingOrganization.value = false;
            }
          }
          stepShown.value += 1;
          logger.debug("=> organization created", createdOrganizationId.value);
          return;
        }

        stepShown.value += 1;
        const previousMetricPromise = lastMetricPromise.value;
        lastMetricPromise.value = (async () => {
          // Awaiting `null` is fine.
          await previousMetricPromise;
        })();
        await lastMetricPromise.value;
      } finally {
        isAdvancingStep.value = false;
      }
    };

    const prevStep = () => {
      if (stepShown.value <= 0) return;
      stepShown.value--;
    };

    return {
      answers,
      isTeam,
      isCompletingOnboarding,
      isCreatingOrganization,
      organizationCreationError,
      nextStep,
      prevStep,
      stepShown,
      hasInitialized,
      createdOrganizationId,
      planStepOrgId,
      planStepUseCase,
      willConfirmPlan,
      preselectedPlan,
      preselectedInterval,
    };
  },
});
</script>
<style scoped lang="scss">
.confetti {
  position: absolute;
  bottom: 50px;
  inset-inline-start: 200px;
}
</style>
